"use client"

import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { LucideIcon } from 'lucide-react'

interface StatsCardProps {
  title: string
  value: string | number
  description: string
  icon: LucideIcon
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'indigo' | 'yellow' | 'pink' | 'cyan'
  loading?: boolean
  onClick?: () => void
  className?: string
}

const colorConfig = {
  blue: {
    border: 'border-l-blue-500',
    iconBg: 'bg-blue-100 dark:bg-blue-950',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  green: {
    border: 'border-l-green-500',
    iconBg: 'bg-green-100 dark:bg-green-950',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  purple: {
    border: 'border-l-purple-500',
    iconBg: 'bg-purple-100 dark:bg-purple-950',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  orange: {
    border: 'border-l-orange-500',
    iconBg: 'bg-orange-100 dark:bg-orange-950',
    iconColor: 'text-orange-600 dark:text-orange-400',
  },
  red: {
    border: 'border-l-red-500',
    iconBg: 'bg-red-100 dark:bg-red-950',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  indigo: {
    border: 'border-l-indigo-500',
    iconBg: 'bg-indigo-100 dark:bg-indigo-950',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
  },
  yellow: {
    border: 'border-l-yellow-500',
    iconBg: 'bg-yellow-100 dark:bg-yellow-950',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  pink: {
    border: 'border-l-pink-500',
    iconBg: 'bg-pink-100 dark:bg-pink-950',
    iconColor: 'text-pink-600 dark:text-pink-400',
  },
  cyan: {
    border: 'border-l-cyan-500',
    iconBg: 'bg-cyan-100 dark:bg-cyan-950',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
  },
}

export function StatsCard({
  title,
  value,
  description,
  icon: Icon,
  color,
  loading = false,
  onClick,
  className = '',
}: StatsCardProps) {
  const colorClasses = colorConfig[color]

  return (
    <Card 
      className={`transition-all duration-200 hover:shadow-lg hover:scale-105 border-l-4 ${colorClasses.border} ${
        onClick ? 'cursor-pointer' : ''
      } ${className}`}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className={`p-2 rounded-full ${colorClasses.iconBg}`}>
          <Icon className={`h-6 w-6 ${colorClasses.iconColor}`} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default StatsCard